<template>
  <div id="MessagingDashboard">
    <jt-dashboard ref="dashboard" :title="$gettext('DASHBOARD')">
      <div class="card col-span-4 row-span-5 panel campaigns-panel">
        <div class="panel-header">
          <div class="panel-title-wrap">
            <span class="panel-title">{{ $gettext('CAMPAIGNS') }}</span>
            <div class="panel-title-line"></div>
          </div>
        </div>
        <div class="campaigns-list" v-if="statistics.campaigns && statistics.campaigns.length">
          <div class="campaign-card" v-for="campaign in statistics.campaigns" :key="campaign.campaignId">
            <div class="campaign-card-header">
              <div class="campaign-main-info">
                <div class="campaign-name-row">
                  <div
                    class="campaign-meta-icons"
                    v-if="resolvePurposeId(campaign) || resolveChannelId(campaign)"
                  >
                    <q-icon
                      v-if="resolvePurposeId(campaign)"
                      :name="getPurposeIcon(resolvePurposeId(campaign))"
                      :style="{ color: getPurposeColor(resolvePurposeId(campaign)), fontSize: '16px' }"
                    >
                      <q-tooltip>{{ resolvePurposeDescription(campaign) }}</q-tooltip>
                    </q-icon>
                    <q-icon
                      v-if="resolveChannelId(campaign)"
                      :name="getChannelIcon(resolveChannelId(campaign))"
                      :style="{ color: getChannelColor(resolveChannelId(campaign)), fontSize: '16px' }"
                    >
                      <q-tooltip>{{ resolveChannelDescription(campaign) }}</q-tooltip>
                    </q-icon>
                  </div>
                  <p class="campaign-name">{{ campaign.name }}</p>
                </div>
                <div class="campaign-section-label">{{ $gettext('EXECUTIONS') }}</div>
                <div class="campaign-badges">
                  <q-badge
                    v-for="([statusId, statusData]) in visibleExecutionStatusCounts(campaign.executionStatusCounts)"
                    :key="statusId"
                    :color="executionCountColor(Number(statusId))"
                    outline
                  >
                    {{ statusData.label }}: {{ formatCount(statusData.count) }}
                  </q-badge>
                </div>
              </div>
              <q-chip
                dense
                :color="campaignStatusColor(campaign.statusId)"
                text-color="white"
                class="campaign-status-chip"
              >
                {{ campaign.status }}
              </q-chip>
            </div>

            <div class="campaign-dispatch-section">
              <div class="campaign-section-label">{{ $gettext('MESSAGING_DASHBOARD_DISPATCHES') }}</div>
              <div class="campaign-badges">
                <q-badge
                  v-for="(statusData, statusId) in campaign.dispatchStatusCounts"
                  :key="statusId"
                  :color="dispatchCountColor(Number(statusId))"
                  outline
                >
                  {{ statusData.label }}: {{ formatCount(statusData.count) }}
                </q-badge>
              </div>
            </div>

            <div class="campaign-actions-row">
              <q-btn
                flat
                round
                dense
                icon="visibility"
                color="primary"
                size="sm"
                :title="$gettext('VIEW')"
                @click="openCampaignDetail(campaign.campaignId)"
              />
              <q-btn
                flat
                round
                dense
                icon="event"
                color="primary"
                size="sm"
                :title="$gettext('MESSAGING_DASHBOARD_PLANNED_DISPATCH_DATES')"
                @click="openCampaignCalendar(campaign)"
              />
            </div>
          </div>
        </div>
        <div v-else class="empty-campaigns">{{ $gettext('MESSAGING_DASHBOARD_NO_CAMPAIGNS_FOUND') }}</div>
      </div>

      <div class="card col-span-8 row-span-5 panel executions-panel">
        <div class="panel-header">
          <div class="panel-title-wrap">
            <span class="panel-title">{{ $gettext('MESSAGING_DASHBOARD_RECENT_EXECUTIONS') }}  </span>
            <div class="panel-title-line"></div>
          </div>
        </div>

        <div class="executions-table-wrapper" v-if="recentExecutions.length">
          <table class="executions-table">
            <thead>
              <tr>
                <th>{{ $gettext('MESSAGING_DASHBOARD_CAMPAIGN') }}</th>
                <th class="text-center">{{ $gettext('MESSAGING_DASHBOARD_DISPATCHES') }}</th>
                <th class="text-center">{{ $gettext('MESSAGING_DASHBOARD_STATUS') }}</th>
                <th class="text-center">{{ $gettext('MESSAGING_DASHBOARD_START') }}</th>
                <th class="text-center">{{ $gettext('MESSAGING_DASHBOARD_TOTAL') }}</th>
                <th class="text-center">{{ $gettext('MESSAGING_DASHBOARD_PROGRESS') }}</th>
                <th class="text-center">{{ $gettext('MESSAGING_DASHBOARD_DELIVERED') }} </th>
                <th class="text-center">{{ $gettext('MESSAGING_DASHBOARD_FAILED') }}</th>
                <th class="text-center">{{ $gettext('MESSAGING_DASHBOARD_RATE') }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="execution in recentExecutions" :key="execution.executionId">
                <td>
                  <div class="execution-campaign-cell">
                    <p class="execution-campaign-name">{{ execution.campaignName || '-' }}</p>
                    <p class="execution-campaign-id">#{{ execution.executionId }}</p>
                  </div>
                </td>

                <td class="text-center">
                  <div class="execution-channel row items-center justify-center q-gutter-xs no-wrap">
                    <q-icon
                      v-if="execution.channelId"
                      :name="getChannelIcon(execution.channelId)"
                      :style="{ color: getChannelColor(execution.channelId), fontSize: '16px' }"
                    />
                    <span>{{ execution.channel || '-' }}</span>
                  </div>
                </td>

                <td>
                  <div class="execution-status" :style="executionStatusStyle(execution.executionStatusCode)">
                    <span class="execution-status-dot"></span>
                    <span>{{ execution.executionStatus || '-' }}</span>
                  </div>
                </td>

                <td class="text-center">{{ execution.startedAt || execution.scheduledDate || '-' }}</td>
                <td class="text-center execution-total">{{ formatCount(execution.total) }}</td>

                <td>
                  <div class="execution-progress-wrap">
                    <div class="execution-progress-track">
                      <div
                        class="execution-progress-fill"
                        :style="executionProgressStyle(execution.progressPercent, execution)"
                      ></div>
                    </div>
                    <span class="execution-progress-label">{{ execution.progressPercent }}%</span>
                  </div>
                </td>

                <td class="text-center execution-delivered">{{ formatCount(execution.delivered) }}</td>
                <td class="text-center execution-failed">{{ formatCount(execution.failed) }}</td>

                <td class="text-center">
                  <span class="execution-rate" :style="executionRateStyle(execution.successRate)">
                    {{ formatPercent(execution.successRate) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="empty-campaigns">{{ $gettext('MESSAGING_DASHBOARD_NO_RECENT_EXECUTIONS_FOUND') }}</div>
      </div>

      <div class="card col-span-6 row-span-5 panel opt-outs-panel">
        <div class="panel-header">
          <div class="panel-title-wrap">
            <span class="panel-title">{{ $gettext('MESSAGING_DASHBOARD_LAST_OPT_OUTS') }}</span>
            <div class="panel-title-line"></div>
          </div>
        </div>
        <div class="opt-outs-list" v-if="optOuts && optOuts.length">
          <div class="opt-out-card" v-for="optOut in optOuts" :key="optOut.id">
            <div class="opt-out-header">
              <span class="opt-out-value email">{{ optOut.email || '-' }}</span>
              <span class="opt-out-value">{{ optOut.createdAt ? formatDateTimeLocal(optOut.createdAt) : '-' }}</span>
            </div>

            <div class="opt-out-row">
              <span class="opt-out-label">{{ $gettext('MESSAGING_DASHBOARD_CONSUMER_UNIT_ABBR') }}</span>
              <span class="opt-out-value">{{ optOut.consumerUnit || '-' }}</span>
            </div>

            <div class="opt-out-row">
              <span class="opt-out-label">{{ $gettext('MESSAGING_DASHBOARD_PURPOSE') }}</span>
              <div class="row items-center q-gutter-x-xs no-wrap">
                <q-icon
                  :name="getPurposeIcon(resolvePurposeId(optOut))"
                  :style="{ color: getPurposeColor(resolvePurposeId(optOut)), fontSize: '16px' }"
                />
                <span class="opt-out-value">{{ resolvePurposeDescription(optOut) }}</span>
              </div>
            </div>

            <div class="opt-out-row">
              <span class="opt-out-label">{{ $gettext('MESSAGING_DASHBOARD_CHANNEL') }}</span>
              <div class="row items-center q-gutter-x-xs no-wrap">
                <q-icon
                  :name="getChannelIcon(resolveChannelId(optOut))"
                  :style="{ color: getChannelColor(resolveChannelId(optOut)), fontSize: '1rem' }"
                  :title="resolveChannelDescription(optOut)"
                />
                <span class="opt-out-value">{{ resolveChannelDescription(optOut) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="empty-campaigns">{{ $gettext('MESSAGING_DASHBOARD_NO_OPT_OUT_FOUND') }}</div>
      </div>
    </jt-dashboard>

    <campaign-detail-modal ref="campaignDetailModal" />
    <campaign-calendar-modal ref="campaignCalendarModal" />
  </div>
</template>

<script>
import { Component, Vue } from 'vue-property-decorator'
import { repository } from 'jtech'
import JtDashboard from 'src/components/dashboard/JtDashboard.vue'
import { campaignStatus } from 'src/shared/inf-tabela-descricao/CampaignStatus.enum.js'
import { campaignExecutionStatus } from 'src/shared/inf-tabela-descricao/CampaignExecutionStatus.enum.js'
import { dispatchStatus } from 'src/shared/inf-tabela-descricao/DispatchStatus.enum.js'
import { getChannelColor, getChannelIcon, getChannelLabel } from '../utils/channelMappings.js'
import { getPurposeColor, getPurposeIcon, getPurposeLabel } from '../utils/purposeMappings.js'
import { formatDateTimeLocal } from 'src/shared/utils/dateUtils.js'
import CampaignDetailModal from '../campaign/CampaignDetailModal.vue'
import CampaignCalendarModal from '../campaign/CampaignCalendarModal.vue'

const CAMPAIGN_STATISTICS_INTERFACE = 'campaign.statistics'
const MESSAGING_OPT_OUT_DATATABLE_INTERFACE = 'messagingOptOut.hql'
const MESSAGING_OPT_OUT_TOP_GRID = 'findTop10ByOrderByIdDesc'
const CAMPAIGN_EXECUTION_DATATABLE_INTERFACE = 'campaignExecution.hql'
const COUNT_MANIFESTS_BY_EXECUTION_AND_DISPATCH_STATUS = 'countManifestsByExecutionAndDispatchStatus'
const COUNT_COMMUNICATION_MANIFESTS_BY_DISPATCH_STATUS = 'countCommunicationManifestsByDispatchStatus'

@Component({
  name: 'MessagingDashboard',
  components: {
    JtDashboard,
    CampaignDetailModal,
    CampaignCalendarModal
  }
})
class MessagingDashboard extends Vue {
  statistics = { campaigns: [] }

  optOuts = []

  executions = []

  campaignStatusEnum = campaignStatus()

  campaignExecutionStatusEnum = campaignExecutionStatus()

  dispatchStatusEnum = dispatchStatus()

  async mounted () {
    await this.loadDashboardData()
    this.$watch(
      () => this.$refs.dashboard.showDashboard,
      (value) => {
        if (value) this.loadDashboardData()
      }
    )
  }

  async loadDashboardData () {
    await this.loadStatistics()
    await Promise.all([this.loadOptOuts(), this.loadExecutions()])
  }

  async loadStatistics () {
    const response = await repository.resolveLogic(CAMPAIGN_STATISTICS_INTERFACE)
    if (response?.campaigns) this.statistics = response
  }

  async loadOptOuts () {
    const { registros } = await repository.resolveDatatableModel(MESSAGING_OPT_OUT_DATATABLE_INTERFACE, {
      grid: MESSAGING_OPT_OUT_TOP_GRID
    })
    this.optOuts = registros || []
  }

  async loadExecutions () {
    const parameters = {
      campaignIds: this.statistics.campaigns.map(c => c.campaignId)
    }

    const [executionResponse, manifestResponse] = await Promise.all([
      repository.resolveDatatableModel(CAMPAIGN_EXECUTION_DATATABLE_INTERFACE, {
        grid: COUNT_MANIFESTS_BY_EXECUTION_AND_DISPATCH_STATUS,
        parameters
      }),
      repository.resolveDatatableModel(CAMPAIGN_EXECUTION_DATATABLE_INTERFACE, {
        grid: COUNT_COMMUNICATION_MANIFESTS_BY_DISPATCH_STATUS,
        parameters
      })
    ])

    this.executions = this.mergeExecutionDispatchRows(
      executionResponse?.registros || [],
      manifestResponse?.registros || []
    )
  }

  mergeExecutionDispatchRows (executionRows = [], manifestRows = []) {
    const metadataByExecutionId = executionRows.reduce((acc, row) => {
      const executionId = Number(row.executionId)
      if (!executionId || acc[executionId]) return acc

      acc[executionId] = row
      return acc
    }, {})

    const mergedRowsByKey = executionRows.reduce((acc, row) => {
      acc[this.executionDispatchRowKey(row)] = { ...row }
      return acc
    }, {})

    manifestRows.forEach(row => {
      const executionId = Number(row.executionId)
      if (!executionId) return

      const metadata = metadataByExecutionId[executionId] || {}
      const key = this.executionDispatchRowKey(row)

      mergedRowsByKey[key] = {
        ...metadata,
        ...(mergedRowsByKey[key] || {}),
        ...row,
        executionId: row.executionId || metadata.executionId,
        campaignId: metadata.campaignId,
        campaignName: metadata.campaignName,
        executionStatusCode: metadata.executionStatusCode,
        executionStatus: metadata.executionStatus,
        purpose: metadata.purpose,
        purposeId: metadata.purposeId,
        channel: metadata.channel,
        channelId: metadata.channelId,
        scheduledDate: metadata.scheduledDate,
        startedAt: metadata.startedAt,
        finishedAt: metadata.finishedAt
      }
    })

    return Object.values(mergedRowsByKey)
  }

  executionDispatchRowKey (row = {}) {
    const executionId = Number(row.executionId) || 0
    const dispatchStatusCode = Number(row.dispatchStatusCode)
    const normalizedDispatchStatusCode = Number.isFinite(dispatchStatusCode) ? dispatchStatusCode : 'none'

    return `${executionId}:${normalizedDispatchStatusCode}`
  }

  get recentExecutions () {
    if (!Array.isArray(this.executions) || !this.executions.length) return []

    const grouped = this.executions.reduce((acc, item) => {
      const executionId = Number(item.executionId)
      if (!executionId) return acc

      if (!acc[executionId]) {
        const campaignChannel = this.resolveCampaignChannel(item.campaignId, item)

        acc[executionId] = {
          executionId,
          campaignId: item.campaignId,
          campaignName: item.campaignName,
          channelId: campaignChannel.id,
          channel: campaignChannel.description,
          executionStatusCode: Number(item.executionStatusCode),
          executionStatus: item.executionStatus,
          startedAt: item.startedAt || null,
          finishedAt: item.finishedAt || null,
          scheduledDate: item.scheduledDate || null,
          total: 0,
          delivered: 0,
          failed: 0,
          queued: 0,
          processing: 0,
          notSent: 0
        }
      }

      const quantity = this.resolveManifestQuantity(item)
      const dispatchCode = Number(item.dispatchStatusCode)
      const current = acc[executionId]

      current.startedAt = this.mostRecentDateLabel(current.startedAt, item.startedAt)
      current.finishedAt = this.mostRecentDateLabel(current.finishedAt, item.finishedAt)
      current.scheduledDate = this.mostRecentDateLabel(current.scheduledDate, item.scheduledDate)

      current.total += quantity

      if (dispatchCode === this.dispatchStatusEnum.SUCCESS.key) current.delivered += quantity
      if ([this.dispatchStatusEnum.FAILED.key, this.dispatchStatusEnum.BOUNCED.key].includes(dispatchCode)) current.failed += quantity
      if (dispatchCode === this.dispatchStatusEnum.QUEUED.key) current.queued += quantity
      if (dispatchCode === this.dispatchStatusEnum.PROCESSING.key) current.processing += quantity
      if (dispatchCode === this.dispatchStatusEnum.NOT_SENT.key) current.notSent += quantity

      return acc
    }, {})

    return Object.values(grouped)
      .map(execution => {
        const normalizedExecution = this.resolveExecutionDisplayStatus(execution)
        const processed = Math.max(execution.total - execution.queued - execution.processing, 0)
        const progressPercent = execution.total > 0
          ? Math.round((processed / execution.total) * 100)
          : this.progressForEmptyExecution(normalizedExecution.executionStatusCode)
        const successRate = execution.total > 0 ? (execution.delivered / execution.total) * 100 : 0

        return {
          ...normalizedExecution,
          progressPercent,
          successRate
        }
      })
      .filter(execution => execution.executionStatusCode !== this.campaignExecutionStatusEnum.CANCELLED.key)
      .filter(execution => this.isExecutionDateVisible(execution))
      .sort((a, b) => this.executionSortValue(b) - this.executionSortValue(a))
      .slice(0, 8)
  }

  isExecutionDateVisible (execution) {
    const executionTimestamp = this.executionDateTimestamp(execution)
    if (!executionTimestamp) return true

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const maxDate = new Date(today)
    maxDate.setDate(maxDate.getDate() + 2)
    maxDate.setHours(23, 59, 59, 999)

    return executionTimestamp <= maxDate.getTime()
  }

  executionDateTimestamp (execution) {
    return this.parsePtBrDateTime(execution.scheduledDate) ||
      this.parsePtBrDateTime(execution.startedAt) ||
      this.parsePtBrDateTime(execution.finishedAt)
  }

  resolveExecutionDisplayStatus (execution) {
    const hasPendingDispatches = execution.queued > 0 || execution.processing > 0
    const hasFinalDispatches = execution.delivered > 0 || execution.failed > 0 || execution.notSent > 0

    if (
      execution.executionStatusCode === this.campaignExecutionStatusEnum.COMPLETED.key &&
      hasPendingDispatches &&
      !hasFinalDispatches
    ) {
      return {
        ...execution,
        executionStatusCode: this.campaignExecutionStatusEnum.PROCESSING.key,
        executionStatus: this.campaignExecutionStatusEnum.PROCESSING.label
      }
    }

    return execution
  }

  progressForEmptyExecution (executionStatusCode) {
    const endedStatuses = [
      this.campaignExecutionStatusEnum.COMPLETED.key,
      this.campaignExecutionStatusEnum.FAILED.key,
      this.campaignExecutionStatusEnum.CANCELLED.key
    ]

    return endedStatuses.includes(executionStatusCode) ? 100 : 0
  }

  executionSortValue (execution) {
    return this.parsePtBrDateTime(execution.startedAt) ||
      this.parsePtBrDateTime(execution.finishedAt) ||
      this.parsePtBrDateTime(execution.scheduledDate) ||
      Number(execution.executionId) || 0
  }

  mostRecentDateLabel (currentDateLabel, nextDateLabel) {
    if (!nextDateLabel) return currentDateLabel || null
    if (!currentDateLabel) return nextDateLabel

    const currentTimestamp = this.parsePtBrDateTime(currentDateLabel)
    const nextTimestamp = this.parsePtBrDateTime(nextDateLabel)

    if (!nextTimestamp) return currentDateLabel
    if (!currentTimestamp) return nextDateLabel

    return nextTimestamp > currentTimestamp ? nextDateLabel : currentDateLabel
  }

  parsePtBrDateTime (value) {
    if (!value || typeof value !== 'string') return null

    const [datePart, timePart = '00:00'] = value.trim().split(' ')
    const [day, month, year] = datePart.split('/').map(Number)
    const [hours = 0, minutes = 0] = timePart.split(':').map(Number)

    if (!day || !month || !year) return null

    const parsed = new Date(year, month - 1, day, hours, minutes)
    const timestamp = parsed.getTime()

    return Number.isNaN(timestamp) ? null : timestamp
  }

  resolveCampaignChannel (campaignId, item = null) {
    const channelId = this.resolveChannelId(item)
    const channelDescription = this.resolveChannelDescription(item)

    if (channelId || channelDescription !== '-') {
      return {
        id: channelId,
        description: channelDescription
      }
    }

    const campaign = this.statistics.campaigns.find(c => Number(c.campaignId) === Number(campaignId))
    const campaignChannelId = this.resolveChannelId(campaign)
    return {
      id: campaignChannelId,
      description: this.resolveChannelDescription(campaign)
    }
  }

  resolveManifestQuantity (item = {}) {
    return Number(item.communicationManifestQuantity ?? item.manifestQuantity ?? 0)
  }

  resolvePurposeId (item = {}) {
    return Number(item.messagingPurposeId || item.messagingPurpose) || null
  }

  resolvePurposeDescription (item = {}) {
    const purposeId = this.resolvePurposeId(item)
    return item.messagingPurposeDescription || item.messagingPurpose_nmDescricao || getPurposeLabel(purposeId) || '-'
  }

  resolveChannelId (item = {}) {
    return Number(item.channelTypeId || item.channelType) || null
  }

  resolveChannelDescription (item = {}) {
    const channelId = this.resolveChannelId(item)
    return item.channelTypeDescription || item.channelType_nmDescricao || getChannelLabel(channelId) || '-'
  }

  executionStatusStyle (statusCode) {
    const color = this.executionStatusHex(statusCode)
    return {
      '--status-color': color
    }
  }

  executionStatusHex (statusCode) {
    const map = {
      [this.campaignExecutionStatusEnum.QUEUED.key]: '#0e88e4',
      [this.campaignExecutionStatusEnum.PROCESSING.key]: '#0e88e4',
      [this.campaignExecutionStatusEnum.COMPLETED.key]: '#00613b',
      [this.campaignExecutionStatusEnum.FAILED.key]: '#dc2626',
      [this.campaignExecutionStatusEnum.CANCELLED.key]: '#6b7280'
    }

    return map[statusCode] || '#6b7280'
  }

  executionProgressStyle (percent, execution) {
    let color = '#0e88e4'
    if (percent === 100 && !execution.failed) color = '#00613b'
    if (execution.failed > 0 && execution.failed >= execution.delivered) color = '#dc2626'

    return {
      width: `${Math.min(Math.max(percent || 0, 0), 100)}%`,
      backgroundColor: color
    }
  }

  executionRateStyle (rate) {
    if (rate >= 90) return { color: '#00613b' }
    if (rate >= 70) return { color: '#d18e4b' }
    return { color: '#dc2626' }
  }

  formatPercent (value) {
    return `${Number(value || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    })}%`
  }

  getChannelColor = getChannelColor

  getChannelIcon = getChannelIcon

  getPurposeIcon = getPurposeIcon

  getPurposeColor = getPurposeColor

  formatDateTimeLocal = formatDateTimeLocal

  campaignStatusColor (statusId) {
    const map = {
      [this.campaignStatusEnum.DRAFT.key]: 'grey',
      [this.campaignStatusEnum.ACTIVE.key]: 'positive',
      [this.campaignStatusEnum.PAUSED.key]: 'warning',
      [this.campaignStatusEnum.ENDED.key]: 'negative'
    }

    return map[statusId] || 'grey'
  }

  executionCountColor (statusId) {
    const map = {
      [this.campaignExecutionStatusEnum.QUEUED.key]: 'blue',
      [this.campaignExecutionStatusEnum.PROCESSING.key]: 'warning',
      [this.campaignExecutionStatusEnum.COMPLETED.key]: 'positive',
      [this.campaignExecutionStatusEnum.FAILED.key]: 'negative',
      [this.campaignExecutionStatusEnum.CANCELLED.key]: 'grey'
    }

    return map[statusId] || 'grey'
  }

  visibleExecutionStatusCounts (executionStatusCounts = {}) {
    return Object.entries(executionStatusCounts).filter(([statusId]) => {
      return Number(statusId) !== this.campaignExecutionStatusEnum.CANCELLED.key
    })
  }

  formatCount (value) {
    return Number(value || 0).toLocaleString('pt-BR')
  }

  openCampaignDetail (id) {
    this.$refs.campaignDetailModal.openDetail(id)
  }

  openCampaignCalendar (campaign) {
    this.$refs.campaignCalendarModal.open(campaign)
  }

  dispatchCountColor (statusId) {
    const map = {
      [this.dispatchStatusEnum.SUCCESS.key]: 'positive',
      [this.dispatchStatusEnum.FAILED.key]: 'negative',
      [this.dispatchStatusEnum.BOUNCED.key]: 'negative',
      [this.dispatchStatusEnum.QUEUED.key]: 'blue',
      [this.dispatchStatusEnum.PROCESSING.key]: 'warning',
      [this.dispatchStatusEnum.NOT_SENT.key]: 'warning'
    }

    return map[statusId] || 'grey'
  }
}

export default MessagingDashboard
</script>

<style lang="stylus" scoped>
#MessagingDashboard
  .panel
    display flex
    flex-direction column
    gap 12px
    border 1px solid #e8ecf0
    border-radius 10px
    overflow hidden

  .panel-title
    font-size 1rem
    font-weight 600
    color #223046

  .campaigns-panel
    overflow hidden

  .executions-panel
    overflow hidden

  .panel-header
    align-items center
    display flex
    justify-content space-between

  .panel-title-wrap
    display inline-block
    position relative
    padding-bottom 6px

  .panel-title-line
    background #d18e4b
    bottom 0
    height 2px
    left 0
    position absolute
    right 0

  .executions-table-wrapper
    flex 1
    overflow auto

  .executions-table
    border-collapse collapse
    min-width 980px
    width 100%

    thead tr
      background #7abcce

    th
      color #fff
      font-size 0.78rem
      font-weight 600
      padding 10px 12px
      text-align left

    td
      border-bottom 1px solid #e8ecf0
      color #2c3f50
      font-size 0.78rem
      padding 10px 12px

    tbody tr
      transition background-color 0.15s ease

      &:hover
        background #f8fafc

  .execution-campaign-cell
    min-width 260px

  .execution-campaign-name
    font-weight 600
    margin 0

  .execution-campaign-id
    color #707070
    font-size 0.7rem
    margin 2px 0 0

  .execution-status
    align-items center
    color #2c3f50
    display flex
    gap 6px
    justify-content center

  .execution-status-dot
    background var(--status-color, #6b7280)
    border-radius 50%
    display inline-block
    height 8px
    width 8px

  .execution-total
    font-weight 600

  .execution-progress-wrap
    align-items center
    display flex
    gap 8px

  .execution-progress-track
    background #f0f0f0
    border-radius 100px
    flex 1
    height 8px
    overflow hidden

  .execution-progress-fill
    border-radius 100px
    height 100%
    transition all 0.3s ease

  .execution-progress-label
    color #707070
    font-size 0.72rem
    min-width 34px
    text-align right

  .execution-delivered
    color #00613b
    font-weight 600

  .execution-failed
    color #dc2626
    font-weight 600

  .execution-rate
    font-weight 700

  .campaigns-list
    display flex
    flex-direction column
    gap 10px
    flex 1
    overflow auto
    padding-right 2px

  .campaign-card
    border 1px solid #e8ecf0
    border-radius 10px
    padding 12px
    transition border-color 0.2s ease

    &:hover
      border-color #7abcce

  .campaign-card-header
    display flex
    justify-content space-between
    align-items flex-start
    gap 10px

  .campaign-main-info
    min-width 0
    flex 1

  .campaign-name
    margin 0
    color #2c3f50
    font-size 0.95rem
    font-weight 600
    white-space nowrap
    overflow hidden
    text-overflow ellipsis

  .campaign-name-row
    display flex
    align-items center
    gap 8px

  .campaign-meta-icons
    display flex
    align-items center
    gap 4px
    color #6e7b88
    flex-shrink 0

  .campaign-status-chip
    font-size 0.75rem

  .campaign-badges
    margin-top 6px
    display flex
    flex-wrap wrap
    gap 4px

  .campaign-dispatch-section
    margin-top 10px

  .campaign-section-label
    font-size 0.72rem
    color #5d6877
    font-weight 600

  .campaign-actions-row
    margin-top 8px
    display flex
    justify-content flex-end
    gap 6px

  .empty-campaigns
    border 1px dashed #d7dee6
    border-radius 10px
    padding 18px
    text-align center
    color #7a8796

  .counts-row
    display flex
    flex-wrap wrap
    gap 4px

  .opt-outs-panel
    overflow hidden

  .opt-outs-list
    display grid
    grid-template-columns repeat(auto-fill, minmax(270px, 1fr))
    grid-auto-rows max-content
    align-items start
    align-content start
    gap 10px
    flex 1
    overflow auto
    padding-right 2px

  .campaigns-panel .empty-campaigns,
  .executions-panel .empty-campaigns,
  .opt-outs-panel .empty-campaigns
    flex 1
    display flex
    align-items center
    justify-content center

  .opt-out-card
    display flex
    flex-direction column
    gap 4px
    border 1px solid #e8ecf0
    border-radius 10px
    padding 8px 10px
    background #fff
    transition border-color 0.2s ease
    align-self start

    &:hover
      border-color #7abcce

  .opt-out-header
    display flex
    justify-content space-between
    align-items center
    margin-bottom 0

  .opt-out-id
    font-weight 700
    color #223046
    font-size 0.82rem

  .opt-out-date
    color #6e7b88
    font-size 0.72rem

  .opt-out-row
    display flex
    align-items center
    justify-content space-between
    gap 8px
    margin-top 0

  .opt-out-label
    font-size 0.7rem
    color #6e7b88
    font-weight 600

  .opt-out-value
    font-size 0.78rem
    color #2c3f50
    font-weight 500
    text-align right

  .email
    max-width 170px
    white-space nowrap
    overflow hidden
    text-overflow ellipsis
</style>
